import { useState, useEffect, useCallback } from "react";
import {
  Box, Typography, Chip, CircularProgress, Alert, IconButton,
  Tooltip, Divider, Select, MenuItem,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import TrainIcon from "@mui/icons-material/Train";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import BoltIcon from "@mui/icons-material/Bolt";

// ─── Config ───────────────────────────────────────────────────────────────────
const ROUTES = [
  { id: "DA-UN", from: "DA", fromLabel: "Danforth GO",    to: "UN", toLabel: "Union Station" },
  { id: "DA-EX", from: "DA", fromLabel: "Danforth GO",    to: "EX", toLabel: "Exhibition GO" },
  { id: "UN-DA", from: "UN", fromLabel: "Union Station",  to: "DA", toLabel: "Danforth GO"   },
  { id: "EX-DA", from: "EX", fromLabel: "Exhibition GO",  to: "DA", toLabel: "Danforth GO"   },
];
const DEFAULT_ROUTE = ROUTES[0];
const GO_GREEN = "#00853F";

// ─── Data fetcher ─────────────────────────────────────────────────────────────
async function fetchSchedule(from, to) {
  const res = await fetch(`/api/go-schedule?from=${from}&to=${to}`);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const data = await res.json();
  if (data.error) throw new Error(data.error);
  return data;
}

// ─── Helpers ──────────────────────────────────────────────────────────────────
function minutesFromNow(orderTime) {
  const diff = new Date(orderTime) - Date.now();
  return Math.round(diff / 60000);
}

function fmtCountdown(mins) {
  if (mins < 0) return "Departed";
  if (mins === 0) return "Now";
  if (mins < 60) return `${mins} min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

// ─── Trip card ────────────────────────────────────────────────────────────────
function TripCard({ trip, isNext, dark, border }) {
  const mins = minutesFromNow(trip.orderTime);
  const departed = mins < -2;
  const imminent = mins >= 0 && mins <= 5;

  return (
    <Box sx={{
      mx: 2, mb: 1.5,
      borderRadius: 1.5,
      border: `1px solid ${isNext ? GO_GREEN : (dark ? "#1e293b" : "#e2e8f0")}`,
      bgcolor: isNext
        ? (dark ? "#052e16" : "#f0fdf4")
        : (dark ? "#0d1220" : "#ffffff"),
      overflow: "hidden",
      opacity: departed ? 0.45 : 1,
    }}>
      {isNext && (
        <Box sx={{ bgcolor: GO_GREEN, px: 1.5, py: 0.4, display: "flex", alignItems: "center", gap: 0.5 }}>
          <BoltIcon sx={{ fontSize: 12, color: "#fff" }} />
          <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#fff", textTransform: "uppercase", letterSpacing: "0.06em" }}>
            Next Train
          </Typography>
        </Box>
      )}
      <Box sx={{ px: 2, py: 1.5 }}>
        {/* Main time row */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
          <Typography sx={{ fontSize: "1.6rem", fontWeight: 800, color: isNext ? GO_GREEN : "text.primary", lineHeight: 1, letterSpacing: "-0.03em" }}>
            {trip.departureTimeDisplay}
          </Typography>
          <ArrowForwardIcon sx={{ fontSize: 16, color: "text.secondary", mx: 0.5 }} />
          <Typography sx={{ fontSize: "1.3rem", fontWeight: 600, color: "text.secondary", lineHeight: 1 }}>
            {trip.arrivalTimeDisplay}
          </Typography>
          <Box sx={{ flex: 1 }} />
          {!departed && (
            <Chip
              label={fmtCountdown(mins)}
              size="small"
              sx={{
                fontWeight: 700,
                fontSize: "0.75rem",
                height: 24,
                bgcolor: imminent ? GO_GREEN : (dark ? "#1e293b" : "#f1f5f9"),
                color: imminent ? "#fff" : "text.secondary",
                border: imminent ? "none" : `1px solid ${dark ? "#334155" : "#e2e8f0"}`,
              }}
            />
          )}
        </Box>

        {/* Details row */}
        <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
          <Chip
            label={`${trip.durationMinutes} min`}
            size="small"
            icon={<AccessTimeIcon sx={{ fontSize: "11px !important" }} />}
            sx={{ height: 20, fontSize: "0.68rem", bgcolor: "transparent", color: "text.secondary", border: `1px solid ${dark ? "#1e293b" : "#e2e8f0"}` }}
          />
          <Chip
            label={`LE ${trip.lines?.[0]?.tripNumber || ""}`}
            size="small"
            sx={{ height: 20, fontSize: "0.68rem", fontWeight: 700, bgcolor: GO_GREEN, color: "#fff" }}
          />
          {trip.lines?.[0]?.isExpress && (
            <Chip label="Express" size="small" sx={{ height: 20, fontSize: "0.68rem", fontWeight: 700, bgcolor: "#7c3aed", color: "#fff" }} />
          )}
          {trip.transfers > 0 && (
            <Chip label={`${trip.transfers} transfer`} size="small" sx={{ height: 20, fontSize: "0.68rem", color: "text.secondary" }} />
          )}
        </Box>
      </Box>
    </Box>
  );
}

// ─── Section header ───────────────────────────────────────────────────────────
function SectionHeader({ label, dark }) {
  return (
    <Typography sx={{
      fontSize: "0.65rem", fontWeight: 700, textTransform: "uppercase",
      letterSpacing: "0.08em", color: "text.secondary",
      px: 2, pt: 2, pb: 0.75,
    }}>
      {label}
    </Typography>
  );
}

// ─── GOSchedulePage ───────────────────────────────────────────────────────────

// ─── TTC headway estimate by hour ─────────────────────────────────────────────
function ttcWaitMins() {
  const h = new Date().getHours();
  if (h >= 7 && h < 10)  return 3;  // AM peak
  if (h >= 10 && h < 16) return 5;  // midday
  if (h >= 16 && h < 19) return 3;  // PM peak
  if (h >= 19 && h < 23) return 6;  // evening
  return 10;                          // late night
}

const TTC_RIDE_MINS = 28; // Union → Main St. midpoint of 26-31 min
const GO_RIDE_MINS  = 17; // Union → Danforth GO

function GetHomeWidget({ nextTrip, dark, border }) {
  const [ttcAlerts, setTtcAlerts] = useState([]);

  useEffect(() => {
    fetch("/api/ttc-subway")
      .then(r => r.ok ? r.json() : [])
      .then(data => {
        if (Array.isArray(data)) {
          setTtcAlerts(data.filter(a =>
            a.route === "2" &&
            (a.effect === "NO_SERVICE" || a.effect === "SIGNIFICANT_DELAYS")
          ));
        }
      })
      .catch(() => {});
  }, []);

  const goWait   = nextTrip ? Math.max(0, Math.round(minutesFromNow(nextTrip.orderTime))) : null;
  const goTotal  = goWait !== null ? goWait + GO_RIDE_MINS : null;
  const ttcWait  = ttcWaitMins();
  const ttcTotal = ttcWait + TTC_RIDE_MINS;

  const hasLine2Alert = ttcAlerts.length > 0;
  const goFaster = goTotal !== null && goTotal <= ttcTotal;

  const goOption = (
    <Box sx={{
      bgcolor: goFaster ? "background.paper" : (dark ? "#0f172a" : "#f8fafc"),
      border: `${goFaster ? 2 : 1}px solid ${goFaster ? GO_GREEN : border}`,
      borderRadius: 2, p: 1.5,
    }}>
      {goFaster && (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5,
          bgcolor: "#00853F", px: 1, py: 0.25, borderRadius: 1, mb: 0.75 }}>
          <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#fff",
            textTransform: "uppercase", letterSpacing: "0.06em" }}>Faster</Typography>
        </Box>
      )}
      <Typography sx={{ fontSize: "0.68rem", color: "text.secondary", textTransform: "uppercase",
        letterSpacing: "0.06em", mb: 0.5 }}>GO Train · Danforth GO</Typography>
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: "1.8rem", fontWeight: 700, color: goFaster ? GO_GREEN : "text.primary",
          lineHeight: 1, letterSpacing: "-0.02em" }}>
          {goTotal !== null ? `${goTotal} min` : "—"}
        </Typography>
        {nextTrip && (
          <Typography sx={{ fontSize: "0.78rem", color: "text.secondary", textAlign: "right" }}>
            Departs {nextTrip.departureTimeDisplay}
          </Typography>
        )}
      </Box>
      <Box sx={{ display: "flex", gap: 1.5, mt: 0.75 }}>
        <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
          🕐 {goWait} min wait
        </Typography>
        <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
          → {GO_RIDE_MINS} min ride
        </Typography>
      </Box>
    </Box>
  );

  const ttcOption = (
    <Box sx={{
      bgcolor: !goFaster ? "background.paper" : (dark ? "#0f172a" : "#f8fafc"),
      border: `${!goFaster ? 2 : 1}px solid ${!goFaster ? "#3b82f6" : border}`,
      borderRadius: 2, p: 1.5,
    }}>
      {!goFaster && (
        <Box sx={{ display: "inline-flex", alignItems: "center", gap: 0.5,
          bgcolor: "#3b82f6", px: 1, py: 0.25, borderRadius: 1, mb: 0.75 }}>
          <Typography sx={{ fontSize: "0.68rem", fontWeight: 700, color: "#fff",
            textTransform: "uppercase", letterSpacing: "0.06em" }}>Faster</Typography>
        </Box>
      )}
      <Typography sx={{ fontSize: "0.68rem", color: "text.secondary", textTransform: "uppercase",
        letterSpacing: "0.06em", mb: 0.5 }}>TTC Line 2 · Main St.</Typography>
      <Box sx={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <Typography sx={{ fontSize: "1.8rem", fontWeight: 700,
          color: !goFaster ? "#3b82f6" : "text.primary", lineHeight: 1, letterSpacing: "-0.02em" }}>
          ~{ttcTotal} min
        </Typography>
        <Typography sx={{ fontSize: "0.78rem", color: "text.secondary", textAlign: "right" }}>
          Est. headway
        </Typography>
      </Box>
      <Box sx={{ display: "flex", gap: 1.5, mt: 0.75 }}>
        <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
          🕐 ~{ttcWait} min wait
        </Typography>
        <Typography sx={{ fontSize: "0.75rem", color: "text.secondary" }}>
          → ~{TTC_RIDE_MINS} min ride
        </Typography>
      </Box>
      {hasLine2Alert && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mt: 1,
          bgcolor: dark ? "#431407" : "#fef3c7", borderRadius: 1, px: 1, py: 0.5 }}>
          <Typography sx={{ fontSize: "0.75rem" }}>⚠️</Typography>
          <Typography sx={{ fontSize: "0.75rem", color: dark ? "#fcd34d" : "#92400e", fontWeight: 500 }}>
            {ttcAlerts[0]?.effectDesc || "Alert"} on Line 2
          </Typography>
        </Box>
      )}
    </Box>
  );

  return (
    <Box sx={{ mx: 2, mb: 2, bgcolor: "background.paper",
      border: `1px solid ${border}`, borderRadius: 2, p: 1.5 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1.25 }}>
        <Typography sx={{ fontSize: "0.75rem" }}>🏠</Typography>
        <Typography sx={{ fontSize: "0.8rem", fontWeight: 700, color: "text.primary" }}>
          Get home from Union
        </Typography>
      </Box>
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
        {goFaster ? <>{goOption}{ttcOption}</> : <>{ttcOption}{goOption}</>}
      </Box>
      <Typography sx={{ fontSize: "0.68rem", color: "text.secondary", mt: 1, textAlign: "center" }}>
        TTC wait is estimated · GO uses live schedule
      </Typography>
    </Box>
  );
}

export default function GOSchedulePage({ colorMode, setColorMode, dark, border, barBg, isMobile }) {
  const [schedule, setSchedule] = useState(null);
  const [loading, setLoading]   = useState(true);
  const [error, setError]       = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshing, setRefreshing]   = useState(false);
  const [now, setNow] = useState(Date.now());
  const [selectedRoute, setSelectedRoute] = useState(DEFAULT_ROUTE);

  // Tick every 30s to keep countdowns fresh
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  const load = useCallback(async (isManual = false) => {
    if (isManual) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const data = await fetchSchedule(selectedRoute.from, selectedRoute.to);
      setSchedule(data);
      setLastRefresh(new Date());
    } catch (e) {
      setError(`Failed to load GO schedule: ${e.message}`);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [selectedRoute]);

  useEffect(() => {
    load();
    const interval = setInterval(() => load(), 5 * 60 * 1000); // refresh every 5 min
    return () => clearInterval(interval);
  }, [load]);

  // Split trips into upcoming and past, find next
  const trips = schedule?.trips || [];
  const upcoming = trips.filter(t => minutesFromNow(t.orderTime) >= 0);
  const past = trips.filter(t => minutesFromNow(t.orderTime) < 0);
  const nextTrip = upcoming[0] || null;
  const laterTrips = upcoming.slice(1);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>

      {/* ── Header ── */}
      <Box sx={{ height: 52, display: "flex", alignItems: "center", px: 2, gap: 1, borderBottom: `1px solid ${border}`, bgcolor: barBg, flexShrink: 0 }}>
        <TrainIcon sx={{ fontSize: 20, color: GO_GREEN }} />
        <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, flexShrink: 0 }}>GO Schedule</Typography>
        <Select
          value={selectedRoute.id}
          onChange={e => setSelectedRoute(ROUTES.find(r => r.id === e.target.value))}
          size="small"
          variant="outlined"
          sx={{
            fontSize: "0.78rem", height: 28, ml: 0.5,
            "& .MuiOutlinedInput-notchedOutline": { borderColor: dark ? "#1e293b" : "#e2e8f0" },
            "& .MuiSelect-select": { py: 0.5, pr: "28px !important", pl: 1 },
            color: "text.secondary",
          }}
        >
          {ROUTES.map(r => (
            <MenuItem key={r.id} value={r.id} sx={{ fontSize: "0.78rem" }}>
              {r.fromLabel} → {r.toLabel}
            </MenuItem>
          ))}
        </Select>
        <Box sx={{ flex: 1 }} />
        {lastRefresh && (
          <Typography variant="caption" sx={{ color: "text.secondary", display: { xs: "none", sm: "block" }, fontSize: "0.72rem" }}>
            {lastRefresh.toLocaleTimeString("en-CA", { hour: "2-digit", minute: "2-digit", hour12: false })}
          </Typography>
        )}
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={() => load(true)} disabled={refreshing} sx={{ color: "text.secondary" }}>
            {refreshing ? <CircularProgress size={14} /> : <RefreshIcon sx={{ fontSize: 20 }} />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* ── Route banner ── */}
      {!loading && !error && (
        <Box sx={{ px: 2, py: 1.25, borderBottom: `1px solid ${border}`, bgcolor: dark ? "#052e16" : "#f0fdf4", display: "flex", alignItems: "center", gap: 1, flexShrink: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: GO_GREEN }} />
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: GO_GREEN }}>{selectedRoute.fromLabel}</Typography>
          </Box>
          <ArrowForwardIcon sx={{ fontSize: 14, color: GO_GREEN }} />
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: GO_GREEN }} />
            <Typography sx={{ fontSize: "0.85rem", fontWeight: 700, color: GO_GREEN }}>{selectedRoute.toLabel}</Typography>
          </Box>
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ fontSize: "0.72rem", color: GO_GREEN, fontWeight: 600 }}>
            {upcoming.length} upcoming
          </Typography>
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mx: 2, mt: 1, fontSize: "0.82rem" }}>{error}</Alert>}

      {/* ── Content ── */}
      <Box sx={{ flex: 1, overflow: "auto", bgcolor: "background.default" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "100%" }}>
            <CircularProgress size={30} sx={{ color: GO_GREEN }} />
          </Box>
        ) : upcoming.length === 0 && past.length === 0 ? (
          <Box sx={{ textAlign: "center", py: 8 }}>
            <Typography variant="body2" sx={{ color: "text.secondary" }}>No trains scheduled for today.</Typography>
          </Box>
        ) : (
          <>
            {/* Get home widget — only for Union → Danforth routes */}
            {(selectedRoute.id === "UN-DA" || selectedRoute.id === "UN-EX") && (
              <GetHomeWidget nextTrip={nextTrip} dark={dark} border={border} />
            )}

            {/* Next train */}
            {nextTrip && (
              <>
                <SectionHeader label="Next departure" dark={dark} />
                <TripCard trip={nextTrip} isNext dark={dark} border={border} now={now} />
              </>
            )}

            {/* Upcoming */}
            {laterTrips.length > 0 && (
              <>
                <SectionHeader label="Later today" dark={dark} />
                {laterTrips.map(trip => (
                  <TripCard key={trip.lines?.[0]?.tripNumber} trip={trip} isNext={false} dark={dark} border={border} now={now} />
                ))}
              </>
            )}

            {/* Past — collapsed */}
            {past.length > 0 && (
              <>
                <Divider sx={{ mx: 2, my: 1 }} />
                <SectionHeader label={`Earlier today · ${past.length} departed`} dark={dark} />
                {past.map(trip => (
                  <TripCard key={trip.lines?.[0]?.tripNumber} trip={trip} isNext={false} dark={dark} border={border} now={now} />
                ))}
              </>
            )}
          </>
        )}
      </Box>

      {/* ── Footer ── */}
      <Box sx={{ height: 52, bgcolor: barBg, borderTop: `1px solid ${border}`, px: 2, display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
        <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem" }}>
          Data: Metrolinx · api.metrolinx.com
        </Typography>
        <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
          <Box sx={{ width: 6, height: 6, borderRadius: "50%", bgcolor: GO_GREEN, animation: "pulse 2s infinite" }} />
          <Typography variant="caption" sx={{ color: "text.secondary", fontSize: "0.65rem" }}>Schedule · 5min</Typography>
        </Box>
      </Box>
    </Box>
  );
}
